import { type ReactNode, useState } from "react";
import { IconButton } from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import {
  type NavigateOptions,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";

import { formatHeaderDate } from "@/app/lib/utils/dates";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import CancelHotelBooking from "./CancelHotelBooking";
import { BackButton } from "../BackButton";
import { HotelSearchForm } from "./HotelSearchForm";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";
import { ProgressStepper } from "../ProgressStepper";

type HotelBookingHeaderProps = {
  className?: string;
  title?: ReactNode;
  onClose?: () => void;
  showStepper?: boolean;
  showSearchForm?: boolean;
} & (
  | {
      showStepper: true;
      step: number;
    }
  | { showStepper?: false }
);

const STEPS_CONFIG = [
  { title: "hotels.title" },
  { title: "hotels.chooseRoom" },
  { title: "travellers.title" },
  { title: "summary" },
  {
    title: "success",
    prev: { to: "/", search: { category: "hotels" } },
  },
] satisfies {
  title: string;
  prev?: NavigateOptions;
}[];

export const HotelBookingHeader = ({
  title,
  onClose,
  showSearchForm,
  ...props
}: HotelBookingHeaderProps) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { history } = useRouter();

  const { searchData } = useHotelSearchDataContext();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  return (
    <>
      <div className="mb-4 flex w-full flex-col px-2">
        <div className="flex items-center justify-between">
          {props.showStepper ? (
            <BackButton
              onClick={() => {
                const prevOptions = STEPS_CONFIG[props.step].prev;
                if (prevOptions) {
                  navigate(prevOptions);
                } else {
                  history.back();
                }
              }}
            />
          ) : (
            <div className="size-6" />
          )}
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="flex w-full items-center justify-center">
              {props.showStepper ? (
                <h3 className="font-medium text-text-title">
                  {t(STEPS_CONFIG[props.step].title)}
                </h3>
              ) : (
                title
              )}
            </div>
            {searchData ? (
              <span className="max-w-[220px] truncate text-xs font-normal text-text-tertiary">
                {formatHeaderDate(searchData.dates.checkIn)} -{" "}
                {formatHeaderDate(searchData.dates.checkOut)},{" "}
                <span>
                  {t("hotels.search.roomCount", {
                    count: searchData.rooms.length,
                  })}
                </span>
              </span>
            ) : null}
          </div>
          <IconButton
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                setIsCancelConfirmOpen(true);
              }
            }}
          >
            <XIcon size={24} color="text-text-title" />
          </IconButton>
        </div>

        {isFeatureEnabled(Feature.HotelsPersistentSearch) && showSearchForm ? (
          <div className="mx-2 mt-4 rounded-4xl bg-white p-3 shadow-sm">
            <HotelSearchForm />
          </div>
        ) : null}

        {props.showStepper ? (
          <div className="my-2 px-2">
            <ProgressStepper
              totalSteps={5} // 0 is a valid step so we need to add 1
              currentStepNumber={props.step + 1}
            />
          </div>
        ) : null}
      </div>

      <CancelHotelBooking
        open={isCancelConfirmOpen}
        closeDrawer={() => setIsCancelConfirmOpen(false)}
      />
    </>
  );
};
